import { Info } from 'lucide-react';
import type { HoraCertaRowImportStatus } from '../../adapter';
import type { SpreadsheetPreview, SpreadsheetSheet } from '../../spreadsheet';

const ROW_STATUS_DETAILS = [
  { label: 'Será importada', color: '#d1fae5' },
  { label: 'Será ignorada', color: '#fee2e2' },
];

const IMPORTED_COLUMNS = new Set([
  'Agendamento',
  'Armazéns',
  'Transportadora',
  'Tomador',
  'Tipo Operação',
  'Dt. Agendamento',
  'Hr. Inicial',
  'Hr. Final',
  'Peso do Agendamento (Kg)',
  'Motorista',
  'CPF',
  'Celular',
  'Placa Veículo',
  'Tipo Veículo',
  'Observação',
  'Operação',
  'Status',
]);

interface HoraCertaPreviewStepProps {
  preview: SpreadsheetPreview;
  sheetName: string;
  onSheetNameChange: (sheetName: string) => void;
  currentSheet?: SpreadsheetSheet;
  rowImportStatuses: HoraCertaRowImportStatus[];
}

export const HoraCertaPreviewStep = ({
  preview,
  sheetName,
  onSheetNameChange,
  currentSheet,
  rowImportStatuses,
}: HoraCertaPreviewStepProps) => {
  const pastCount = rowImportStatuses.filter(
    (status) => status === 'past'
  ).length;
  const cancelledCount = rowImportStatuses.filter(
    (status) => status === 'cancelled'
  ).length;
  const quarantineCount = rowImportStatuses.filter(
    (status) => status === 'quarantine'
  ).length;
  const ignoredCount = pastCount + cancelledCount + quarantineCount;
  const processableCount = rowImportStatuses.length - ignoredCount;
  const allRowsArePast = pastCount === rowImportStatuses.length;

  return (
    <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="flex flex-col gap-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-GilroySemibold text-base text-slate-900 mb-3">
            Arquivo carregado
          </h2>
          <p className="text-sm font-semibold text-slate-800 break-all">
            {preview.fileName}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {(preview.fileSize / 1024).toFixed(1)} KB
          </p>
          <span className="mt-3 inline-flex items-center px-3 py-1 rounded-md bg-emerald-50 text-emerald-600 text-xs font-semibold">
            Planilha carregada
          </span>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="font-GilroySemibold text-base text-slate-900 mb-3">
            Resumo
          </h2>
          <label
            className="block text-xs text-slate-600 mb-1"
            htmlFor="sheet-select"
          >
            Aba da planilha
          </label>
          <select
            id="sheet-select"
            value={sheetName}
            onChange={(event) => onSheetNameChange(event.target.value)}
            className="w-full text-sm font-sans rounded-md border border-slate-300 py-2 px-3 bg-white"
          >
            {preview.sheets.map((sheet) => (
              <option key={sheet.name} value={sheet.name}>
                {sheet.name}
              </option>
            ))}
          </select>
          <dl className="mt-4 space-y-2 text-sm text-slate-600">
            <div className="flex justify-between">
              <dt>Linhas encontradas:</dt>
              <dd className="font-bold text-slate-900">
                {currentSheet?.rows.length ?? 0}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt>Colunas encontradas:</dt>
              <dd className="font-bold text-slate-900">
                {currentSheet?.columns.length ?? 0}
              </dd>
            </div>
            <div className="flex justify-between text-emerald-700">
              <dt>Serão processadas:</dt>
              <dd className="font-bold">{processableCount}</dd>
            </div>
            <div className="flex justify-between text-red-700">
              <dt>Serão ignoradas:</dt>
              <dd className="font-bold">{ignoredCount}</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="mb-4 flex items-center gap-2">
          <div className="flex items-center gap-2">
            <h2 className="font-GilroySemibold text-base text-slate-900">
              Pré-visualização
            </h2>
            <span className="group relative inline-flex">
              <button
                type="button"
                aria-label="Informações sobre as colunas importadas"
                aria-describedby="imported-columns-tooltip"
                className="text-slate-400 hover:text-[#EE7C2C] focus:text-[#EE7C2C] focus:outline-none"
              >
                <Info aria-hidden="true" size={16} />
              </button>
              <span
                id="imported-columns-tooltip"
                role="tooltip"
                className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-md bg-slate-900 px-3 py-2 text-xs font-normal text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
              >
                As colunas destacadas são consideradas na importação. As demais
                são exibidas apenas para conferência e não serão processadas.
              </span>
            </span>
          </div>
        </div>
        {ignoredCount > 0 && (
          <div
            role={allRowsArePast ? 'alert' : 'status'}
            className={`mb-4 rounded-lg border px-4 py-3 text-sm ${
              allRowsArePast
                ? 'border-red-200 bg-red-50 text-red-800'
                : 'border-amber-200 bg-amber-50 text-amber-800'
            }`}
          >
            {allRowsArePast ? (
              <>
                <strong>Nenhum registro poderá ser importado.</strong> Todas as
                linhas possuem data de agendamento anterior à data atual. Revise
                a planilha antes de continuar.
              </>
            ) : (
              <>
                <strong>
                  {processableCount} de {rowImportStatuses.length} registro(s)
                  serão processado(s).
                </strong>{' '}
                {ignoredCount} registro(s) possuem data de agendamento anterior
                à data atual, Status CANCELADO ou ainda estão pendentes na
                quarentena e serão ignorados.
              </>
            )}
          </div>
        )}
        <div
          aria-label="Legenda do resultado esperado"
          className="mb-2 flex flex-wrap justify-end gap-x-3 gap-y-1 text-xs text-slate-600"
        >
          {ROW_STATUS_DETAILS.map((details) => (
            <span key={details.label} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-3 w-3 rounded-sm"
                style={{ backgroundColor: details.color }}
              />
              {details.label}
            </span>
          ))}
        </div>
        <div className="overflow-auto max-h-[60vh]">
          <table className="min-w-max text-left text-xs">
            <thead>
              <tr className="sticky top-0 z-10 bg-slate-50 border-b border-slate-200 text-slate-800">
                {(currentSheet?.columns || []).map((column) => (
                  <th
                    key={column}
                    className={`py-2 px-3 font-bold ${
                      IMPORTED_COLUMNS.has(column)
                        ? 'bg-orange-100 text-orange-900'
                        : ''
                    }`}
                  >
                    {column}
                    {IMPORTED_COLUMNS.has(column) && (
                      <>
                        <span aria-hidden="true"> *</span>
                        <span className="sr-only">
                          {' '}
                          (considerada na importação)
                        </span>
                      </>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(currentSheet?.rows || []).map((row, index) => {
                const status = rowImportStatuses[index] || 'invalid-date';
                const willBeIgnored =
                  status === 'past' ||
                  status === 'cancelled' ||
                  status === 'quarantine';
                const result = ROW_STATUS_DETAILS[willBeIgnored ? 1 : 0];

                return (
                  <tr
                    key={index}
                    aria-label={`Status da linha: ${result.label}`}
                  >
                    {(currentSheet?.columns || []).map((column) => (
                      <td
                        key={column}
                        className="py-2 px-3 text-slate-700"
                        style={{ backgroundColor: result.color }}
                      >
                        {String(row[column] ?? '')}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
